import React, { useEffect, useState } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import BarRoundedChart from '../../../../../components/ui/charts/BarRoundedChart';
import CustomActionButton from '../../../../../components/ui/buttons/CustomActionButton';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import Button from '../../../../../components/ui/buttons/Button';
import { CirclePlus } from 'lucide-react';
import DreamModal from '../../../../../components/modals/dreams/DreamModal';
import DreamCardPriority from '../../../../../components/cards/DreamCardPriority';
import DreamCard from '../../../../../components/cards/DreamCard';
import RetirementScenarioCard from '../../../../../components/cards/RetirementScenarioCard';
import RetirementScenariosModal from '../../../../../components/modals/dreams/RetirementScenariosModal';
import BlurScopeContent from '../../../../../components/ui/overlays/BlurScopeContent';
import CurrencyText from '../../../../../components/ui/labels/CurrencyText';

const DreamsTab = ({ clientId, onSuccess, scope, canWrite }) => {
  const [data, setData] = useState({
    currentMonthContributions: 0,
    monthlyContributions: [],
    totalContributions: 0,
    totalContributed: 0,
    totalWithdrawn: 0,
    realContributed: 0,
    expectedRealReturn: 0,
    dreams: [],
    retirementScenarios: [],
    retirementChartData: [],
    spouseId: null,
  });

  const [showCreateDreamModal, setShowCreateDreamModal] = useState(false);
  const [selectedDreamId, setSelectedDreamId] = useState(null);
  const [showRetirementScenariosModal, setShowRetirementScenariosModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/dreams?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  const handleModalSuccess = () => {
    setShowCreateDreamModal(false);
    setSelectedDreamId(null);
    setShowRetirementScenariosModal(false);
    fetchData();
  };

  const tabBody = (
    <div className="grid grid-cols-6 gap-5">
      {/* col 1 */}
      <div className="flex flex-col col-span-2 gap-5">
        {/* aportes */}
        <div className="flex flex-col bg-white rounded-custom20 px-6 py-4 gap-4">
          <div className="flex items-center justify-between mt-4">
            <span className="text-lg font-semibold text-blueDarker">Aportes mensais</span>
            <CustomActionButton className="h-7" state="alt" onClick={() => console.log('resgate')}>
              EXPORTAR RELATÓRIO
            </CustomActionButton>
          </div>

          <div className="flex items-center justify-between border-grayLight border-t border-b py-5">
            <span className="text-base font-normal text-blueSteelDark">Total aportado no mês atual</span>

            <CurrencyText value={data.currentMonthContributions} size="large" />
          </div>

          <div className="flex flex-col py-1">
            <span className="text-base font-normal text-blueSteelDark">Aportes dos meses anteriores</span>
            <BarRoundedChart data={data.monthlyContributions} className="h-64" />
          </div>
        </div>

        <div className="flex flex-col bg-white rounded-custom20 px-6 py-4 gap-4">
          <div className="flex items-center justify-between py-2">
            <span className="text-lg font-semibold text-blueDarker">Total geral aportado real</span>

            <CurrencyText value={data.realContributed} size="large" />
          </div>

          <div className="flex items-center justify-between border-grayLight border-t border-b py-5">
            <span className="text-base font-normal text-blueSteelDark">Total geral de aportes</span>

            <CurrencyText value={data.totalContributed} size="medium" />
          </div>

          <div className="flex items-center justify-between py-5">
            <span className="text-base font-normal text-blueSteelDark">Total geral de retiradas</span>

            <CurrencyText value={data.totalWithdrawn} size="medium" color="text-redSoft" />
          </div>
        </div>

        <div className="flex flex-col bg-white rounded-custom20 px-6 py-4 gap-4">
          <div className="flex items-center justify-between py-2">
            <span className="text-lg font-semibold text-blueDarker">Expectativa de retorno real</span>
            <span className="text-blueHighlight font-semibold text-lg">
              <span className="text-4xl">{data.expectedRealReturn}%</span>
            </span>
          </div>
        </div>
      </div>

      {/* col 2 */}
      <div className="rounded-custom20 col-span-4 flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-4">
          <RetirementScenarioCard
            key="retirement-scenario-card"
            retirementScenarios={data.retirementScenarios}
            retirementChartData={data.retirementChartData}
            onEdit={() => setShowRetirementScenariosModal(true)}
          />

          {data.dreams.map((dream) => (
            <DreamCard key={dream.id} dream={dream} onEdit={setSelectedDreamId} canEdit={canWrite} />
          ))}
        </div>

        {canWrite && (
          <div className="flex justify-center items-center">
            <div className="flex gap-3">
              <Button icon={CirclePlus} disabled={scope === 'couple'} onClick={() => setShowCreateDreamModal(true)}>
                CADASTRAR NOVO SONHO
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      {scope === 'couple' ? (
        <BlurScopeContent
          title='Indisponível para a visualização "casal"'
          subtitle="Altere para Cliente ou Cônjuge para ver e editar os sonhos."
        >
          <div className="pointer-events-none select-none">{tabBody}</div>
        </BlurScopeContent>
      ) : (
        tabBody
      )}

      {/* Monta modais apenas quando NÃO estiver borrado */}
      {scope !== 'couple' && canWrite && showCreateDreamModal && (
        <DreamModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowCreateDreamModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {scope !== 'couple' && selectedDreamId && (
        <DreamModal
          clientId={clientId}
          dreamId={selectedDreamId}
          scope={scope}
          onClose={() => setSelectedDreamId(null)}
          onSuccess={handleModalSuccess}
          canWrite={canWrite}
        />
      )}

      {scope !== 'couple' && showRetirementScenariosModal && (
        <RetirementScenariosModal
          clientId={clientId}
          scope={scope}
          onClose={handleModalSuccess}
          onSuccess={handleModalSuccess}
          canWrite={canWrite}
        />
      )}
    </div>
  );
};

export default DreamsTab;
